/**
 * RbAnswers Repository
 * 
 * Phase E4: Draft Answer Persistence
 * Immutable persistence - new AI run = new record
 */

import { 
  Firestore, 
  collection, 
  doc, 
  getDoc, 
  getDocs,
  query, 
  where, 
  orderBy,
  setDoc,
  Timestamp 
} from 'firebase/firestore';
import type { RbAnswer, CreateAnswerInput } from './types/rb.js';

const COLLECTION = 'rb_answers';

/**
 * Create a new answer
 * All fields required
 */
export async function createAnswer(
  db: Firestore,
  answerId: string,
  input: CreateAnswerInput
): Promise<void> {
  const answer: RbAnswer = {
    id: answerId,
    workspaceId: input.workspaceId,
    projectId: input.projectId,
    questionId: input.questionId,
    aiRunId: input.aiRunId,
    draftText: input.draftText,
    citations: input.citations,
    warnings: input.warnings,
    assumptions: input.assumptions,
    confidenceScore: input.confidenceScore,
    createdAt: Timestamp.now(),
    createdByUserId: input.createdByUserId,
  };

  const docRef = doc(db, COLLECTION, answerId);
  await setDoc(docRef, answer);
}

/**
 * Get answer by ID
 */
export async function getAnswer(
  db: Firestore,
  answerId: string
): Promise<RbAnswer | null> {
  const docRef = doc(db, COLLECTION, answerId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return null;
  }
  
  return docSnap.data() as RbAnswer;
}

/**
 * List answers for a question (ordered by creation time, newest first)
 */
export async function listAnswersForQuestion(
  db: Firestore,
  questionId: string
): Promise<RbAnswer[]> {
  const q = query(
    collection(db, COLLECTION),
    where('questionId', '==', questionId),
    orderBy('createdAt', 'desc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => doc.data() as RbAnswer);
}

/**
 * Get answer by AI run ID
 */
export async function getAnswerByAiRunId(
  db: Firestore,
  aiRunId: string
): Promise<RbAnswer | null> {
  const q = query(
    collection(db, COLLECTION),
    where('aiRunId', '==', aiRunId)
  );
  
  const snapshot = await getDocs(q);
  
  if (snapshot.empty) {
    return null;
  }
  
  return snapshot.docs[0].data() as RbAnswer;
}

/**
 * List answers for a project
 */
export async function listAnswersForProject(
  db: Firestore,
  projectId: string
): Promise<RbAnswer[]> {
  const q = query(
    collection(db, COLLECTION),
    where('projectId', '==', projectId),
    orderBy('createdAt', 'desc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => doc.data() as RbAnswer);
}
